import { colors, ui } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactNode } from 'react'
import { StageControls } from 'room/common/stage-surface'

/**
 * The knowledge check on the in-person stage: the stage's whole width (the
 * stage pads its sides, so none here), the check's own card shape, and two
 * panes under its header - the content on the left, 60%, and the participants
 * on the right, 40%.
 *
 * The height is the stage's less the 20px the word cloud leaves under its
 * card, so the foot of both is on the same line. It is stretched rather than
 * `100%`, or the margin would push the card past the stage.
 */
const Card = styled.div`
	align-self: stretch;
	background-color: ${colors.white};
	border-radius: 42px 18px 18px 18px;
	box-shadow: 0 3px 6px 0 ${colors.black15};
	display: flex;
	flex-direction: column;
	margin-bottom: 20px;
	min-height: 0;
	overflow: hidden;
	width: 100%;
`

const Header = styled.div`
	flex: none;
`

const Panes = styled.div`
	display: grid;
	flex: 1 1 0;
	grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
	min-height: 0;
`

const Pane = styled.div`
	min-height: 0;
	min-width: 0;
	overflow-y: auto;
	padding: 32px 40px;

	${ui.responsive.desktopFHD} {
		padding: 40px 56px;
	}
`

const Content = styled(Pane)`
	display: flex;
	flex-direction: column;
`

const Side = styled(Pane)`
	border-left: 1px solid ${colors.borderLightGray};
`

/**
 * Like the quiz's Abort and Check: the quiet action in the bottom left corner,
 * the main one in the bottom right. Gone with its controls on the projector.
 */
const Actions = styled.div`
	align-items: center;
	border-top: 1px solid ${colors.borderLightGray};
	display: flex;
	flex: none;
	gap: 16px;
	justify-content: space-between;
	padding: 20px 32px;
`

const ActionGroup = styled.div`
	align-items: center;
	display: flex;
	gap: 16px;
`

type Props = {
	/** The check's header: the trainer, the title and, in the review, its progress. */
	header: ReactNode
	/** What the participants panel is called for assistive tech. */
	sideLabel: string
	/** The participants panel. */
	side: ReactNode
	/** Bottom left. */
	startActions?: ReactNode
	/** Bottom right. */
	endActions?: ReactNode
}

const StageCard: FC<Props> = ({
	header,
	sideLabel,
	side,
	startActions,
	endActions,
	children,
}) => (
	<Card>
		<Header>{header}</Header>
		<Panes>
			<Content>{children}</Content>
			<Side as="aside" aria-label={sideLabel}>
				{side}
			</Side>
		</Panes>
		<StageControls>
			<Actions>
				<ActionGroup>{startActions}</ActionGroup>
				<ActionGroup>{endActions}</ActionGroup>
			</Actions>
		</StageControls>
	</Card>
)

export default StageCard
